import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { I18nService } from './local/i18n.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base/services';
import { CacheSettings } from '../core/http/cache.interceptor';
import { __ } from '../functions/object.functions';
import { Response } from './local/Response';


export class EntityService<T, TCreate, TUpdate> extends BaseService {

  // -----------------------------------------------------------------------------------------------------
  // @ CONSTRUCTOR
  // -----------------------------------------------------------------------------------------------------

  constructor(
    public endpoint: string,
    protected entity: string,
    protected httpService: HttpClient,
    protected i18nService: I18nService
  ) {
    super();
  }

  getAll(
    queryParameters: string = '',
    skip: number = 0,
    take: number = 20,
    cacheOptions: CacheSettings = null,
    withAccessToken: boolean = true,
    path: string = ''
  ): Observable<T[]> {
    const _skip = __.IsNullOrUndefined(skip) ? 0 : skip;
    const _take = __.IsNullOrUndefined(take) ? 20 : take;
    const _queryParameters = __.IsNullOrUndefinedOrEmpty(queryParameters) ? '' : `&${queryParameters}`;

    let httpClient = this.httpService;

    if (!__.IsNullOrUndefined(cacheOptions)) {
      httpClient = httpClient.cache(cacheOptions, false);
    } else {
      httpClient = httpClient.cache(null, true);
    }

    if (withAccessToken === false) {
      httpClient = httpClient.disableAccessToken();
    }

    return httpClient.get<Response<T[]>>(`${this.endpoint}${path}?skip=${_skip}&take=${_take}${_queryParameters}`).pipe(
      map((response: Response<T[]>) => {
        return response.data;
      })
    );
  }

  /*
      Used to retrieve child
    */
  getAllChildren<TChild>(
    id: string,
    childEndpoint: string,
    queryParameters: string = '',
    skip: number = 0,
    take: number = 20,
    enableCache: boolean = false): Observable<TChild[]> {
    const _skip = __.IsNullOrUndefined(skip) ? 0 : skip;
    const _take = __.IsNullOrUndefined(take) ? 20 : take;
    const _enableCache = __.IsNullOrUndefined(enableCache) ? false : enableCache;
    const _queryParameters = __.IsNullOrUndefinedOrEmpty(queryParameters) ? '' : `&${queryParameters}`;

    let httpClient = this.httpService;
    if (_enableCache === true) {
      httpClient = httpClient.cache();
    } else {
      httpClient = httpClient.cache(null, true);
    }

    return this.httpService
      .skipErrorHandler()
      .get<Response<TChild[]>>(`${this.endpoint}/${id}/${childEndpoint}?skip=${_skip}&take=${_take}${_queryParameters}`)
      .pipe(
        map((response: Response<TChild[]>) => {
          return response.data;
        })
      )
  }

  getById(
    id: string,
    filterParameters: string = '',
    withAccessToken: boolean = true,
    withAllLanguages: boolean = false
  ): Observable<T> {
    let httpClient = this.httpService;

    if (withAccessToken === false) {
      httpClient = httpClient.disableAccessToken();
    }

    let headers = new HttpHeaders();

    headers = headers.set('Accept-Language', this.i18nService.language);

    if (withAllLanguages === true) {
      headers = headers.set('Accept-Language', this.i18nService.supportedLanguages.map(q => q.locale).join(','));
    }

    return httpClient.disableAcceptedLanguage().get<Response<T>>(
      `${this.endpoint}/${id}?${filterParameters}`,
      { headers }
    ).pipe(
      map((response: Response<T>) => {
        return response.data;
      })
    );
  }

  create(entity: TCreate, withAccessToken: boolean = true): Observable<T> {
    console.log('create', entity);
    let httpClient = this.httpService;

    if (withAccessToken === false) {
      httpClient = httpClient.disableAccessToken();
    }

    return httpClient.skipErrorHandler().post<Response<T>>(`${this.endpoint}`, entity).pipe(
      map((response: Response<T>) => {
        return response.data;
      })
    );
  }

  update(id: string, entity: TUpdate): Observable<T> {
    return this.httpService.patch<Response<T>>(`${this.endpoint}/${id}`, entity).pipe(
      map((response: Response<T>) => {
        return response.data;
      })
    );
  }

  deleteById(id: string): Observable<boolean> {
    return this.httpService.delete(`${this.endpoint}/${id}`, { observe: 'response' }).pipe(
      map((response: HttpResponse<any>) => {
        return response.status === 204;
      })
    );
  }
}
